import { useState, useMemo } from 'react';

const adjustDayIndex = (dayIndex: number) => (dayIndex === 0 ? 6 : dayIndex - 1);

const getCalendarDays = (month: number, year: number) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = adjustDayIndex(new Date(year, month, 1).getDay());
  const lastDayOfMonth = adjustDayIndex(new Date(year, month, daysInMonth).getDay());

  const days: Array<{
    day: string;
    number: number;
    isCurrentMonth: boolean;
    fullDate: Date;
  }> = [];

  if (firstDayOfMonth !== 0) {
    const prevMonthDays = new Date(year, month, 0).getDate();
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthDays - i);
      days.push({
        day: date.toLocaleString('en-US', { weekday: 'short' }).charAt(0),
        number: date.getDate(),
        isCurrentMonth: false,
        fullDate: date,
      });
    }
  }

  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(year, month, i);
    days.push({
      day: date.toLocaleString('en-US', { weekday: 'short' }).charAt(0),
      number: i,
      isCurrentMonth: true,
      fullDate: date,
    });
  }

  if (lastDayOfMonth !== 6) {
    for (let i = 1; i <= 6 - lastDayOfMonth; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        day: date.toLocaleString('en-US', { weekday: 'short' }).charAt(0),
        number: date.getDate(),
        isCurrentMonth: false,
        fullDate: date,
      });
    }
  }

  return days;
};

const groupDaysIntoWeeks = (days: Array<{ day: string; number: number; isCurrentMonth: boolean; fullDate: Date }>) => {
  const weeks: Array<Array<{ day: string; number: number; isCurrentMonth: boolean; fullDate: Date }>> = [];
  let week: Array<{ day: string; number: number; isCurrentMonth: boolean; fullDate: Date }> = [];
  days.forEach((day, index) => {
    week.push(day);
    if ((index + 1) % 7 === 0 || index === days.length - 1) {
      weeks.push(week);
      week = [];
    }
  });
  return weeks;
};

export const getCurrentWeekDates = (currentDate: Date): Date[] => {
  const dayOfWeek = currentDate.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const startOfWeek = new Date(currentDate);
  startOfWeek.setDate(currentDate.getDate() + mondayOffset);

  return Array.from({ length: 7 }, (_, i) => {
    const weekDate = new Date(startOfWeek);
    weekDate.setDate(startOfWeek.getDate() + i);
    return weekDate;
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getStartAndEndOfMonth = (weeks: any, currentDate: any) => {
  let startDate = null;
  let endDate = null;

  for (const week of weeks) {
    for (const day of week) {
      if (day.isCurrentMonth) {
        const year = new Date(currentDate).getFullYear();
        const month = new Date(currentDate).getMonth();
        const date = new Date(year, month, day.number);

        if (!startDate || date < startDate) {
          startDate = date;
        }

        if (!endDate || date > endDate) {
          endDate = date;
        }
      }
    }
  }

  return {
    startDate: startDate,
    endDate: endDate,
  };
};

const formatWeekRange = (weekDates: Date[]): string => {
  if (weekDates.length !== 7) {
    throw new Error('Invalid week dates array. It must contain 7 dates.');
  }

  const formatDate = (date: Date): string => {
    const day = date.getDate();
    const daySuffix =
      day === 1 || day === 21 || day === 31
        ? 'st'
        : day === 2 || day === 22
          ? 'nd'
          : day === 3 || day === 23
            ? 'rd'
            : 'th';

    return `${day}${daySuffix}`;
  };

  const month = weekDates[0].toLocaleString('en-US', { month: 'short' });
  const start = formatDate(weekDates[0]);
  const end = formatDate(weekDates[6]);

  return `${month} ${start} - ${end}`;
};

export const useCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isCalendarView, setIsCalendarView] = useState(false);

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const calendarDays = useMemo(() => getCalendarDays(currentMonth, currentYear), [currentMonth, currentYear]);
  const weeks = useMemo(() => groupDaysIntoWeeks(calendarDays), [calendarDays]);

  const weekDates = useMemo(() => getCurrentWeekDates(currentDate), [currentDate]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { endDate, startDate } = useMemo(() => getStartAndEndOfMonth(weeks, currentDate), [currentDate]);
  const weekRangeText = useMemo(() => formatWeekRange(weekDates), [weekDates]);
  const monthlyRangeText = useMemo(
    () => currentDate.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
    [currentDate],
  );

  const moveDate = (direction: 'forward' | 'backward') => {
    const nextDate = new Date(currentDate);
    const offset = direction === 'forward' ? 1 : -1;

    nextDate.setMonth(nextDate.getMonth() + offset);

    setCurrentDate(nextDate);
  };

  const weekdays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return {
    isCalendarView,
    setIsCalendarView,
    currentDate,
    moveDate,
    weeks,
    weekdays,
    weekDates,
    startDate,
    endDate,
    weekRangeText,
    monthlyRangeText,
  };
};
